import { EnumProductTypes } from "src/types/listing";
import { PropertyDetails } from "../common/PropertyDetails";
import { PropertyMarkerProps } from "../neighborhoodSearch/components/PropertyMarker";
import { getListingStatusColors } from "src/utils/listings";
import PropertyMarker from "../neighborhoodSearch/components/PropertyMarker";
import { LISTING_STATUS_COLORS } from "src/constants/listing";
import { useMemo, useState, useEffect } from "react";
import { useListingDetailsQuery } from "src/hooks/useListing";
import SectionHeader from "../listingDetails/SectionHeader";

export type ListingPropertyMarkerProps = PropertyMarkerProps & {
  listingId: string;
  productType: EnumProductTypes;
  isSelected?: boolean;
  children?: React.ReactNode;
  isHideDNCPhoneNumbers?: boolean | null | undefined;
  suppressPriceInDetailsPanel?: boolean;
};

const ListingPropertyMarker = ({
  listingId,
  productType,
  isSelected,
  children,
  isHideDNCPhoneNumbers,
  suppressPriceInDetailsPanel = false,
  isHovered,
  ...otherProps
}: ListingPropertyMarkerProps) => {
  const [shouldFetchDetails, setShouldFetchDetails] = useState(false);

  // Only fetch details when marker is selected or hovered
  useEffect(() => {
    if (isSelected || isHovered) {
      setShouldFetchDetails(true);
    }
  }, [isSelected, isHovered]);

  const { data: listing, isLoading, error } = useListingDetailsQuery(
    productType,
    listingId,
    { enabled: shouldFetchDetails, isHideDNCPhoneNumbers: isHideDNCPhoneNumbers ?? false }
  );

  const extension = useMemo(() => {
    if (!listing) return LISTING_STATUS_COLORS.GREEN.color;

    if (productType === EnumProductTypes.distressed) {
      return listing.distressedLevel;
    }
    return getListingStatusColors(listing.status || "")?.color || LISTING_STATUS_COLORS.GREEN.color;
  }, [productType, listing]);

  // Always render the marker, but only show details when data is loaded
  const showDetails = shouldFetchDetails && !isLoading && !error && listing;

  return (
    <PropertyMarker
      productType={productType}
      extension={extension}
      isSelected={isSelected}
      isHovered={isHovered}
      {...otherProps}
    >
      {showDetails ? (
        <div className="w-[350px]">
          <PropertyDetails
            isContactEditingDisabled
            isShowStarredIconWithTitle
            isSuppressContactCards
            listing={listing}
            isSuppressPreview
          />
          {!suppressPriceInDetailsPanel && listing && (
            <div>
              <SectionHeader title="Price" className="!mb-1" />
              <p className="text-[16px] leading-6 whitespace-nowrap text-gray-primary">
                {listing.formattedPrice}
              </p>
            </div>
          )}
          {children}
        </div>
      ) : (
        <div className="w-[350px] p-4">
          {isLoading ? (
            <p className="text-gray-500">Loading...</p>
          ) : (
            <p className="text-gray-500">Click or hover to view details</p>
          )}
        </div>
      )}
    </PropertyMarker>
  );
};

export default ListingPropertyMarker;
